import { useCallback, useState } from 'react';
import { useDialog } from './useDialog';
import { Sizes } from 'enums/sizes';
import { DocumentUrlDto, DocumentsUrlResponse } from 'api/files';
import { DocumentScopeEnum } from 'api/documents';
import { filesAPI } from 'services/swagger-api';
import { MultiDocumentViewer } from 'components/domain/Document/MultiDocumentViewer';

export type DocumentViewerStrategy =
  | typeof DocumentScopeEnum.Contract
  | typeof DocumentScopeEnum.Quote
  | typeof DocumentScopeEnum.Receipt;

interface DocumentFetchConfig {
  strategy: DocumentViewerStrategy;
  entityId: string;
}

const fetchContractDocuments = async (
  contractId: string
): Promise<DocumentUrlDto[]> => {
  const response: DocumentsUrlResponse =
    await filesAPI.getContratTemporaryDocumentsUrls({
      contratId: contractId
    });
  return response.urls || [];
};

const fetchQuoteDocuments = async (
  quoteId: string
): Promise<DocumentUrlDto[]> => {
  const response: DocumentsUrlResponse =
    await filesAPI.getDevisTemporaryDocumentsUrls({
      devisId: quoteId
    });
  return response.urls || [];
};

const fetchReceiptDocuments = async (
  receiptId: string
): Promise<DocumentUrlDto[]> => {
  // Mock pour l'instant - à implémenter quand l'API sera disponible
  console.log('Mock: fetchReceiptDocuments for receiptId:', receiptId);
  return [
    {
      fileName: 'Quittance_mock.pdf',
      url: 'https://example.com/mock-receipt.pdf'
    }
  ];
};

const getDocumentsFetcher = (
  strategy: DocumentViewerStrategy
): ((entityId: string) => Promise<DocumentUrlDto[]>) => {
  switch (strategy) {
    case DocumentScopeEnum.Contract:
      return fetchContractDocuments;
    case DocumentScopeEnum.Quote:
      return fetchQuoteDocuments;
    case DocumentScopeEnum.Receipt:
      return fetchReceiptDocuments;
    default:
      throw new Error(`Unknown document strategy: ${strategy}`);
  }
};

const getDialogTitle = (strategy: DocumentViewerStrategy): string => {
  switch (strategy) {
    case DocumentScopeEnum.Contract:
      return 'Documents du contrat';
    case DocumentScopeEnum.Quote:
      return 'Documents du devis';
    case DocumentScopeEnum.Receipt:
      return 'Documents de la quittance';
    default:
      return 'Documents';
  }
};

export const useDocumentViewerDialog = () => {
  const { openBlankDialog, closeDialog } = useDialog();
  const [isLoading, setIsLoading] = useState(false);

  const openDocumentViewer = useCallback(
    async (config: DocumentFetchConfig) => {
      const { strategy, entityId } = config;
      setIsLoading(true);

      try {
        const fetcher = getDocumentsFetcher(strategy);
        const documents = await fetcher(entityId);
        const title = getDialogTitle(strategy);

        openBlankDialog(
          { size: Sizes['5xl'] },
          <MultiDocumentViewer
            title={title}
            documents={documents}
            onClose={closeDialog}
          />
        );
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [openBlankDialog, closeDialog]
  );

  return {
    openDocumentViewer,
    isLoading
  };
};
